import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { toast } from 'react-toastify';
import * as api from '../api/endpoints';

const AuthContext = createContext(null);

// Sessions last until the end of the working day: you sign in once and stay signed in
// until midnight, then are asked to sign in again the next day (no idle timeout).
const ACTIVITY_KEY = 'helpdesk_last_activity';
const SESSION_DAY_KEY = 'helpdesk_session_day';
const todayStr = () => {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
};

// Shared by both the password login and the SSO login below -- both API
// calls (auth/login/, auth/sso-login/) return the exact same
// { access, employee } shape, so storing the result is identical either way.
const _storeSession = (data, setEmployee) => {
  localStorage.setItem('helpdesk_access_token', data.access);
  localStorage.setItem('helpdesk_last_activity', String(Date.now()));
  localStorage.setItem(SESSION_DAY_KEY, todayStr());
  localStorage.setItem('helpdesk_employee', JSON.stringify(data.employee));
  setEmployee(data.employee);
  return data.employee;
};

export const AuthProvider = ({ children }) => {
  const [employee, setEmployee] = useState(() => {
    const stored = localStorage.getItem('helpdesk_employee');
    return stored ? JSON.parse(stored) : null;
  });

  const login = useCallback(async (username, password) => {
    const data = await api.login(username, password);
    return _storeSession(data, setEmployee);
  }, []);

  // Office.mavistech.uk single sign-on: called by the /sso route with the
  // one-time code that arrived in the iframe's URL (see pages/SsoLogin.js).
  const ssoLogin = useCallback(async (code) => {
    const data = await api.ssoLogin(code);
    return _storeSession(data, setEmployee);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('helpdesk_access_token');
    localStorage.removeItem('helpdesk_employee');
    localStorage.removeItem(SESSION_DAY_KEY);
    setEmployee(null);
  }, []);

  // Patches the cached employee record (e.g. after saving a self-service
  // preference) without a full re-login.
  const updateEmployee = useCallback((patch) => {
    setEmployee((prev) => {
      const next = { ...prev, ...patch };
      localStorage.setItem('helpdesk_employee', JSON.stringify(next));
      return next;
    });
  }, []);

  useEffect(() => {
    if (!employee) return undefined;
    const stamp = () => localStorage.setItem(ACTIVITY_KEY, String(Date.now()));
    if (!localStorage.getItem(ACTIVITY_KEY)) stamp();
    let lastWrite = 0;
    const onActivity = () => {
      const t = Date.now();
      if (t - lastWrite > 10000) { lastWrite = t; stamp(); }
    };
    // A session that predates this change counts as today's.
    if (!localStorage.getItem(SESSION_DAY_KEY)) localStorage.setItem(SESSION_DAY_KEY, todayStr());
    const check = () => {
      if (localStorage.getItem(SESSION_DAY_KEY) !== todayStr()) {
        localStorage.removeItem(ACTIVITY_KEY);
        logout();
        toast.info('Your session ended at the end of the day. Please sign in again.', { autoClose: false });
      }
    };
    const events = ['mousedown', 'mousemove', 'keydown', 'touchstart', 'scroll', 'wheel', 'click'];
    events.forEach((ev) => window.addEventListener(ev, onActivity, { passive: true }));
    document.addEventListener('visibilitychange', check);
    window.addEventListener('focus', check);
    const timer = setInterval(check, 30000);
    check();
    return () => {
      events.forEach((ev) => window.removeEventListener(ev, onActivity));
      document.removeEventListener('visibilitychange', check);
      window.removeEventListener('focus', check);
      clearInterval(timer);
    };
  }, [employee?.mavis_user_id, logout]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <AuthContext.Provider value={{ employee, login, ssoLogin, logout, updateEmployee, isAuthenticated: !!employee }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
